import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";
import { DateInput } from "./ui/date-input";

export const todayIso = () => new Date().toISOString().slice(0, 10);

/** Έκδοση Πράξης ανά υπόθεση: Αρ. Πράξης + υποχρεωτική Ημερομηνία Έκδοσης + μορφή (Μονομελές & Πολυμελές). */
export const ActIssueDialog = ({ open, onOpenChange, title, description, year, defaultActNumber = "", suggestActNumber,
  defaultIssueDate, initialFormat = "docx", formats = ["docx", "pdf", "odt"], onConfirm, testPrefix = "act-issue",
  showNoteOption = false, defaultIncludeNote = true, noteHint = "" }) => {
  const [actNum, setActNum] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [format, setFormat] = useState(initialFormat);
  const [includeNote, setIncludeNote] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActNum(String(defaultActNumber || ""));
    setIssueDate(defaultIssueDate || todayIso());
    setFormat(initialFormat);
    setIncludeNote(defaultIncludeNote);
    if (!defaultActNumber && suggestActNumber) {
      Promise.resolve(suggestActNumber()).then((n) => { if (n) setActNum(String(n)); }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = async () => {
    if (!String(actNum).trim()) return toast.error("Δώστε Αρ. Πράξης");
    if (!issueDate) return toast.error("Η Ημερομηνία Έκδοσης είναι υποχρεωτική");
    setBusy(true);
    try {
      const ok = await onConfirm({ actNumber: String(actNum).trim(), issueDate, format, includeNote: showNoteOption ? includeNote : undefined });
      if (ok !== false) onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-700" />{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-2">
          <div>
            <Label className="text-xs">Αρ. Πράξης <span className="text-red-600">*</span></Label>
            <Input value={actNum} onChange={(e) => setActNum(e.target.value)} placeholder={`π.χ. 12 (έτος ${year || ""})`} className="h-9" data-testid={`${testPrefix}-act-number`} />
          </div>
          <div>
            <Label className="text-xs">Ημερομηνία Έκδοσης <span className="text-red-600">*</span></Label>
            <DateInput value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="h-9" required data-testid={`${testPrefix}-issue-date`} />
          </div>
          <div>
            <Label className="text-xs">Μορφή</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="h-9" data-testid={`${testPrefix}-format`}><SelectValue /></SelectTrigger>
              <SelectContent>{formats.map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {showNoteOption && (
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer rounded-md border border-slate-200 bg-slate-50 p-2">
            <input type="checkbox" className="mt-0.5" checked={includeNote} onChange={(e) => setIncludeNote(e.target.checked)} data-testid={`${testPrefix}-include-note`} />
            <span>Συμπερίληψη της επισήμανσης <em>«Επισημαίνεται ότι η νόμιμη προθεσμία διενέργειας του ανωτέρω προδικαστικού ελέγχου εκκινεί από …»</em> (11η ημέρα μετά το κλείσιμο της Προσθήκης){noteHint ? ` — ${noteHint}` : ""}</span>
          </label>
        )}
        <p className="text-xs text-slate-500">Η Πράξη εκδίδεται με την Ημερομηνία Έκδοσης που δηλώνετε και «παγώνει»: κάθε επόμενη λήψη (Word/PDF) επιστρέφει ακριβώς αυτήν την πράξη.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testPrefix}-cancel`}>Άκυρο</Button>
          <Button onClick={confirm} disabled={busy || !issueDate || !String(actNum).trim()} className="bg-blue-700 hover:bg-blue-800" data-testid={`${testPrefix}-confirm`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Έκδοση Πράξης
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
